import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';

const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function ymd(d) {
  return d.toISOString().slice(0, 10);
}

function mondayOf(d) {
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + diff));
}

export default function VisitSchedule() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const qc = useQueryClient();

  const [mode, setMode] = useState('week'); // 'week' | 'list'
  const [weekStart, setWeekStart] = useState(() => ymd(mondayOf(new Date())));
  const [repFilter, setRepFilter] = useState(null); // admin-only
  const [city, setCity] = useState(''); // lower(trim(city)) key from /customers/cities

  const reps = useQuery({
    queryKey: ['schedule-reps'],
    queryFn: () => api.get('/notes/reps').then(r => r.data.data),
    enabled: isAdmin,
  });

  const cities = useQuery({
    queryKey: ['customer-cities'],
    queryFn: () => api.get('/customers/cities').then(r => r.data.data),
    staleTime: 5 * 60 * 1000,
  });
  const cityLabel = cities.data?.find(c => c.key === city)?.city || '';

  const viewingOther = isAdmin && repFilter != null;

  const board = useQuery({
    queryKey: ['schedule', weekStart, repFilter, city],
    queryFn: () => api.get('/schedule', {
      params: {
        week: weekStart,
        ...(repFilter ? { user_id: repFilter } : {}),
        ...(city ? { city } : {}),
      },
    }).then(r => r.data.data),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['schedule'] });

  const assign = useMutation({
    mutationFn: ({ customer_id, scheduled_date }) => api.post('/schedule', { customer_id, scheduled_date }).then(r => r.data.data),
    onSuccess: invalidate,
  });
  const complete = useMutation({
    mutationFn: (id) => api.put(`/schedule/${id}/complete`).then(r => r.data.data),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/schedule/${id}`).then(r => r.data),
    onSuccess: invalidate,
  });

  const days = useMemo(() => {
    if (!board.data) return [];
    return board.data.days.map((d, i) => ({ ...d, label: DAY_LABELS[i] }));
  }, [board.data]);

  const shiftWeek = (deltaWeeks) => {
    const cur = new Date(`${weekStart}T00:00:00Z`);
    cur.setUTCDate(cur.getUTCDate() + deltaWeeks * 7);
    setWeekStart(ymd(mondayOf(cur)));
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="section-eyebrow">Plan</div>
          <h1 className="text-2xl">Schedule</h1>
          <div className="rule-crimson" />
          <p className="text-xs text-muted mt-1">
            Assign customers from the Due-for-Visit queue to a day this week.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            className="input text-sm"
            aria-label="Filter by city"
            value={city}
            onChange={e => setCity(e.target.value)}
          >
            <option value="">All cities</option>
            {cities.data?.map(c => <option key={c.key} value={c.key}>{c.city} ({c.count})</option>)}
          </select>
          {isAdmin && reps.data?.length > 0 && (
            <select
              className="input text-sm"
              value={repFilter ?? ''}
              onChange={e => setRepFilter(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">My schedule</option>
              {reps.data.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
            </select>
          )}
          <div className="flex gap-1">
            <button
              className={mode === 'week' ? 'btn-primary' : 'btn-ghost'}
              onClick={() => setMode('week')}
            >Week</button>
            <button
              className={mode === 'list' ? 'btn-primary' : 'btn-ghost'}
              onClick={() => setMode('list')}
            >List</button>
          </div>
          <Link to="/customers/due-for-visit" className="btn-ghost">📍 Due for visit</Link>
        </div>
      </header>

      {viewingOther && (
        <div className="card p-2 text-xs text-muted">
          Viewing {reps.data?.find(r => r.id === repFilter)?.full_name}'s schedule — read only.
        </div>
      )}

      {board.isLoading && <div className="text-muted">Loading…</div>}
      {board.isError && (
        <div className="text-crimson">Failed to load — {board.error?.response?.data?.error || 'unknown error'}</div>
      )}

      {board.data && mode === 'week' && (
        <WeekBoard
          days={days}
          weekStart={weekStart}
          onPrev={() => shiftWeek(-1)}
          onNext={() => shiftWeek(1)}
          onToday={() => setWeekStart(ymd(mondayOf(new Date())))}
          canManage={!viewingOther}
          onComplete={id => complete.mutate(id)}
          onRemove={id => remove.mutate(id)}
          onMove={(customer_id, date) => assign.mutate({ customer_id, scheduled_date: date })}
        />
      )}
      {/* Week mode's own unscheduled rail sits below the grid (operator-decided placement,
          2026-08-27) — List mode already shows unscheduled as its own column, so the rail
          would just duplicate it there. */}
      {board.data && mode === 'week' && (
        <UnscheduledRail
          cityLabel={cityLabel}
          unscheduled={board.data.unscheduled}
          canManage={!viewingOther}
          onAssign={(customer_id, scheduled_date) => assign.mutate({ customer_id, scheduled_date })}
          days={days}
        />
      )}

      {board.data && mode === 'list' && (
        <ListView
          cityLabel={cityLabel}
          unscheduled={board.data.unscheduled}
          days={days}
          canManage={!viewingOther}
          onAssign={(customer_id, scheduled_date) => assign.mutate({ customer_id, scheduled_date })}
          onComplete={id => complete.mutate(id)}
          onRemove={id => remove.mutate(id)}
        />
      )}
    </div>
  );
}

function DayPicker({ days, onPick, label = 'Assign to…' }) {
  return (
    <select
      className="input text-xs"
      defaultValue=""
      onChange={e => {
        if (e.target.value) onPick(e.target.value);
        e.target.value = '';
      }}
    >
      <option value="" disabled>{label}</option>
      {days.map(d => <option key={d.date} value={d.date}>{d.label} {d.date.slice(5)}</option>)}
    </select>
  );
}

function VisitCard({ v, canManage, onComplete, onRemove, onMove, days }) {
  return (
    <div className="card p-2 text-xs space-y-1">
      <div className="flex items-start justify-between gap-1">
        <Link to={`/customers/${v.customer_id}`} className="font-medium hover:text-crimson">
          {v.customer_name}
        </Link>
        {canManage && (
          <button className="text-muted hover:text-crimson leading-none" title="Remove" onClick={() => onRemove(v.id)}>×</button>
        )}
      </div>
      <div className="text-muted">{[v.city, v.state].filter(Boolean).join(', ') || '—'}</div>
      {canManage && (
        <div className="flex gap-1 items-center pt-1">
          <button className="btn-ghost text-xs px-2 py-1" onClick={() => onComplete(v.id)}>✓ Complete</button>
          <DayPicker days={days} label="Move…" onPick={date => onMove(v.customer_id, date)} />
        </div>
      )}
    </div>
  );
}

function WeekBoard({ days, weekStart, onPrev, onNext, onToday, canManage, onComplete, onRemove, onMove }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button className="btn-ghost text-sm" onClick={onPrev}>← Prev</button>
        <button className="btn-ghost text-sm" onClick={onToday}>This week</button>
        <button className="btn-ghost text-sm" onClick={onNext}>Next →</button>
        <span className="text-xs text-muted">Week of {weekStart}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        {days.map(d => (
          <div key={d.date} className="space-y-2">
            <div className="text-xs font-semibold text-navy">{d.label} <span className="text-muted font-normal">{d.date.slice(5)}</span></div>
            <div className="space-y-2">
              {d.visits.length === 0 && <div className="text-xs text-muted">—</div>}
              {d.visits.map(v => (
                <VisitCard key={v.id} v={v} canManage={canManage} onComplete={onComplete} onRemove={onRemove}
                  onMove={onMove} days={days} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListView({ cityLabel, unscheduled, days, canManage, onAssign, onComplete, onRemove }) {
  const scheduled = days.flatMap(d => d.visits.map(v => ({ ...v, dayLabel: d.label })));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <div className="text-xs font-semibold text-crimson uppercase tracking-wide mb-2">
          Scheduled this week{cityLabel && ` in ${cityLabel}`}
        </div>
        <div className="space-y-1">
          {scheduled.length === 0 && <div className="text-muted text-sm">Nothing scheduled yet.</div>}
          {scheduled.map(v => (
            <div key={v.id} className="card p-3 flex items-center justify-between gap-2">
              <div>
                <Link to={`/customers/${v.customer_id}`} className="font-medium hover:text-crimson">{v.customer_name}</Link>
                <div className="text-xs text-muted">{[v.city, v.state].filter(Boolean).join(', ') || '—'}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-navy font-semibold">{v.dayLabel}</span>
                {canManage && (
                  <>
                    <button className="btn-ghost text-xs px-2 py-1" onClick={() => onComplete(v.id)}>✓</button>
                    <button className="text-muted hover:text-crimson" onClick={() => onRemove(v.id)}>×</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold text-crimson uppercase tracking-wide mb-2">
          {cityLabel ? `All unscheduled customers in ${cityLabel} (${unscheduled.length})` : 'Unscheduled / overdue'}
        </div>
        <div className="space-y-1">
          {unscheduled.length === 0 && <div className="text-muted text-sm">All caught up.</div>}
          {unscheduled.map(c => (
            <div key={c.id} className="card p-3 flex items-center justify-between gap-2">
              <div>
                <Link to={`/customers/${c.id}`} className="font-medium hover:text-crimson">{c.name}</Link>
                <div className="text-xs text-muted">
                  {[c.city, c.state].filter(Boolean).join(', ') || '—'}
                  {' · '}
                  <span className={c.days_since_touch > 90 ? 'text-crimson' : ''}>{c.days_since_touch}d</span>
                </div>
              </div>
              {canManage && <DayPicker days={days} onPick={date => onAssign(c.id, date)} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Always-visible rail below the week grid (and shown under the list view too,
// operator-decided placement 2026-08-27) so overdue customers are never a
// click away, regardless of which mode is active.
// With a city selected the rail can hold every customer in that city (hundreds
// for Fresno), so it wraps into a scrollable grid instead of one long row.
function UnscheduledRail({ cityLabel, unscheduled, canManage, onAssign, days }) {
  if (!unscheduled || unscheduled.length === 0) {
    return cityLabel
      ? <div className="text-xs text-muted">Every active customer in {cityLabel} is already scheduled.</div>
      : null;
  }
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-crimson uppercase tracking-wide">
        {cityLabel
          ? `All unscheduled customers in ${cityLabel} (${unscheduled.length})`
          : `Unscheduled / overdue (${unscheduled.length})`}
      </div>
      <div className={cityLabel
        ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 max-h-[32rem] overflow-y-auto pb-2'
        : 'flex gap-2 overflow-x-auto pb-2'}>
        {unscheduled.map(c => (
          <div key={c.id} className={cityLabel
            ? 'card p-2 text-xs space-y-1'
            : 'card p-2 text-xs min-w-[180px] flex-shrink-0 space-y-1'}>
            <Link to={`/customers/${c.id}`} className="font-medium hover:text-crimson">{c.name}</Link>
            <div className="text-muted">
              <span className={c.days_since_touch > 90 ? 'text-crimson' : ''}>{c.days_since_touch}d</span> since touch
            </div>
            {canManage && <DayPicker days={days} onPick={date => onAssign(c.id, date)} />}
          </div>
        ))}
      </div>
    </div>
  );
}
